import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import {
  FaChevronLeft,
  FaEye,
  FaHome,
  FaCalendarDay,
  FaClock,
  FaPhone,
  FaUser,
  FaTimes,
  FaCheck,
  FaStore,
} from "react-icons/fa";
import { IoPerson, IoSparkles } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { getHomeSalonOrdersByClientId } from "../../redux/order/OrderSalonHomeSlice";
import { FaArrowsRotate } from "react-icons/fa6";

const SalonOrders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { homeSalonOrders } = useSelector((store) => store.OrderSalon);
  const { loginData, isAuth, clientid } = useSelector(
    (store) => store.Authentication
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Get client ID
  const getClientId = useCallback(() => {
    return loginData?._id || clientid;
  }, [loginData, clientid]);

  const clientId = getClientId();

  // Enhanced contact dialing function with better validation
  const openDialer = useCallback((phoneNumber) => {
    if (!phoneNumber || phoneNumber === "To be assigned") {
      alert("No Number", "Partner number not available yet");
      return;
    }

    // Remove all non-digit and non-plus characters
    const cleanedNumber = phoneNumber.replace(/[^\d+]/g, "");

    if (!cleanedNumber) {
      alert("Invalid Number", "The phone number format is invalid");
      return;
    }

    const phoneUrl = `tel:${cleanedNumber}`;

    // For web, we can provide more options
    const confirmCall = window.confirm(
      `Call: ${cleanedNumber}\n\nWould you like to call this number?`
    );

    if (confirmCall) {
      // Try to use tel protocol, fallback to manual dial suggestion
      window.location.href = phoneUrl;
      setTimeout(() => {
        alert(
          "Phone Dial",
          `If the call didn't start automatically:\n\nDial: ${cleanedNumber}\n\nYou can manually dial this number.`
        );
      }, 1000);
    }
  }, []);

  // Enhanced refresh function with better error handling
  const refreshOrders = useCallback(async () => {
    if (!isAuth || !clientId) {
      return;
    }

    try {
      setRefreshing(true);
      await dispatch(getHomeSalonOrdersByClientId(clientId)).unwrap();
    } catch (error) {
      console.error("Refresh error:", error);
      // Optionally show a toast or notification
      if (error.message) {
        alert("Refresh Failed", "Unable to refresh orders. Please try again.");
      }
    } finally {
      setRefreshing(false);
    }
  }, [isAuth, clientId, dispatch]);

  // Auto-refresh with cleanup and better interval management
  useEffect(() => {
    if (!isAuth || !clientId) return;

    refreshOrders(); // Initial refresh

    const intervalId = setInterval(() => {
      refreshOrders();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuth, clientId, refreshOrders]);

  // Partner details - Enhanced with fallbacks
  const getPartnerDetails = useCallback((order) => {
    return {
      name:
        order?.partnerName ||
        order?.technician ||
        order?.assignedProfessional ||
        "To be assigned",
      number:
        order?.partnerNumber || order?.technicianContact || "To be assigned",
      status:
        order?.partnerName || order?.technician
          ? "Assigned"
          : "Pending Assignment",
      assignedAt: order?.assignedAt || null,
    };
  }, []);

  // Enhanced status mapping with better color scheme (from code 2)
  const getOrderStatusForCustomer = useCallback((order) => {
    const status = order?.Status;

    // Status color mapping from code 2
    const statusColors = {
      1: { text: "Pending", color: "warning", hex: "#FDCB6E" },
      2: { text: "Assigned", color: "primary", hex: "#0984E3" },
      3: { text: "Work Started", color: "info", hex: "#00B894" },
      4: { text: "Work In Progress", color: "warning", hex: "#FFA500" },
      5: { text: "Completed", color: "success", hex: "#28a745" },
      0: { text: "Cancelled", color: "danger", hex: "#FF4757" },
    };

    // Status description mapping
    const statusDescriptions = {
      1: "Your order is being processed and will be assigned shortly.",
      2: "A professional has been assigned to your service.",
      3: "Service has started at your location.",
      4: "Service is currently in progress.",
      5: "Service completed successfully. Thank you for choosing us!",
      0: "This order has been cancelled. Contact support for details.",
    };

    if (status !== undefined && statusColors[status]) {
      return {
        text: statusColors[status].text,
        color: statusColors[status].color,
        hexColor: statusColors[status].hex,
        description: statusDescriptions[status] || statusDescriptions[1],
        statusCode: status,
      };
    }

    // Fallback for string-based status
    const fallbackStatus =
      order?.CurrentStatus?.toLowerCase() ||
      order?.StatusText?.toLowerCase() ||
      "pending";

    const statusMap = {
      pending: {
        text: "Pending",
        color: "warning",
        hexColor: "#FDCB6E",
        description: "Your order is being processed",
      },
      confirmed: {
        text: "Confirmed",
        color: "primary",
        hexColor: "#0984E3",
        description: "Order confirmed and awaiting assignment",
      },
      assigned: {
        text: "Assigned",
        color: "primary",
        hexColor: "#0984E3",
        description: "Professional assigned to your service",
      },
      "work started": {
        text: "Work Started",
        color: "info",
        hexColor: "#00B894",
        description: "Service has started at your location",
      },
      "work in progress": {
        text: "Work In Progress",
        color: "warning",
        hexColor: "#FFA500",
        description: "Service is in progress",
      },
      "in progress": {
        text: "Work In Progress",
        color: "warning",
        hexColor: "#FFA500",
        description: "Service is in progress",
      },
      completed: {
        text: "Completed",
        color: "success",
        hexColor: "#28a745",
        description: "Service completed successfully",
      },
      delivered: {
        text: "Completed",
        color: "success",
        hexColor: "#28a745",
        description: "Service completed successfully",
      },
      cancelled: {
        text: "Cancelled",
        color: "danger",
        hexColor: "#FF4757",
        description: "Order has been cancelled",
      },
    };

    return statusMap[fallbackStatus] || statusMap["pending"];
  }, []);

  const OrderStatusBadge = ({ order, size = "normal" }) => {
    const statusInfo = getOrderStatusForCustomer(order);
    const badgeSize = size === "large" ? "px-4 py-2" : "px-3 py-2";
    const fontSize = size === "large" ? "fs-6" : "";

    return (
      <span
        className={`badge bg-${statusInfo.color} ${badgeSize} rounded-pill fw-semibold ${fontSize}`}
        style={{ backgroundColor: statusInfo.hexColor }}
      >
        {statusInfo.text}
      </span>
    );
  };

  const showOrderDetailsModal = useCallback((order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  }, []);

  // Close modal with escape key support
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && modalVisible) {
        setModalVisible(false);
      }
    };

    if (modalVisible) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [modalVisible]);

  // Order card component with enhanced features
  const OrderCard = ({ order }) => {
    const mainProduct = order.OrderProducts?.[0];
    const partner = getPartnerDetails(order);
    const statusInfo = getOrderStatusForCustomer(order);
    const hasMultipleServices = order.OrderProducts?.length > 1;

    return (
      <div
        className="bg-white rounded-3 shadow-sm mb-4 border border-light position-relative"
        style={{
          cursor: "pointer",
          transition: "all 0.3s ease",
          borderLeft: `4px solid ${statusInfo.hexColor}`,
        }}
        onClick={() => showOrderDetailsModal(order)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "";
        }}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1 me-3">
              <h5
                className="fw-bold text-dark mb-1"
                style={{ fontSize: "14px" }}
              >
                Home Salon Service
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                Order #{order._id?.slice(-8)?.toUpperCase()}
              </p>
            </div>
            <OrderStatusBadge order={order} />
          </div>

          <div className="mb-3" style={{ fontSize: "13px" }}>
            <div className="row g-3 mb-2">
              <div className="col-12 col-md-6 d-flex align-items-center">
                <FaCalendarDay className="text-muted me-2" />
                <span className="text-dark fw-medium">
                  {order.ServicePreferedDate || "Not scheduled"}
                </span>
              </div>
              <div className="col-12 col-md-6 d-flex align-items-center">
                <FaClock className="text-muted me-2" />
                <span className="text-dark fw-medium">
                  {order.ServicePreferedTime || "Not scheduled"}
                </span>
              </div>
            </div>

            <div className="row g-3 mb-2">
              <div className="col-12 col-md-6 d-flex align-items-center">
                <IoPerson className="text-muted me-2" />
                <span className="text-dark fw-medium">{partner.name}</span>
              </div>
              <div
                className="col-12 col-md-6 d-flex align-items-center"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openDialer(partner.number);
                }}
              >
                <FaPhone className="text-muted me-2" />
                <span
                  className="text-primary fw-medium"
                  style={{ textDecoration: "underline" }}
                >
                  {partner.number}
                </span>
              </div>
            </div>

            {/* Enhanced Status Description */}
            <div
              className="p-2 rounded-2 mt-2"
              style={{
                backgroundColor: `${statusInfo.hexColor}15`,
                borderLeft: `3px solid ${statusInfo.hexColor}`,
              }}
            >
              <p
                className="mb-0 text-center small"
                style={{ color: statusInfo.hexColor }}
              >
                {statusInfo.description}
              </p>
            </div>
          </div>

          {mainProduct && (
            <div className="bg-light p-2 rounded-3 mb-3 position-relative">
              {hasMultipleServices && (
                <span
                  className="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-primary"
                  style={{ fontSize: "10px", padding: "3px 6px" }}
                >
                  {order.OrderProducts.length}
                </span>
              )}
              <div className="d-flex align-items-center">
                <img
                  src={mainProduct.ImgUrl}
                  alt={mainProduct.ProductName}
                  className="rounded-2 me-3"
                  style={{ width: "70px", height: "70px", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/70x70?text=Service";
                  }}
                />
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-1">{mainProduct.ProductName}</h6>
                  <p className="text-muted small mb-1">{mainProduct.Brand}</p>
                  <p className="text-primary small mb-1">
                    {mainProduct.CatName}
                  </p>
                  {hasMultipleServices && (
                    <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1">
                      +{order.OrderProducts.length - 1} more service
                      {order.OrderProducts.length - 1 > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div
            className="d-flex justify-content-between align-items-center pt-3 border-top"
            style={{ fontSize: "12px" }}
          >
            <div className="text-muted">
              Scheduled on{" "}
              {moment(order.createdAt).format("DD MMM YYYY, hh:mm A")}
            </div>
            <div className="d-flex align-items-center gap-2">
              {refreshing && (
                <span className="badge bg-warning bg-opacity-10 text-warning px-2 py-1">
                  <FaArrowsRotate size={10} className="me-1" />
                  Updating...
                </span>
              )}
              <button
                className="btn btn-outline-primary btn-sm d-flex align-items-center"
                style={{fontSize:"12px",whiteSpace:"nowrap"}}
                onClick={(e) => {
                  e.stopPropagation();
                  showOrderDetailsModal(order);
                }}
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Modal component with better UX
  const OrderDetailsModal = () => {
    if (!selectedOrder) return null;

    const partner = getPartnerDetails(selectedOrder);
    const statusInfo = getOrderStatusForCustomer(selectedOrder);
    const hasMultipleServices = selectedOrder.OrderProducts?.length > 1;

    return (
      <div
        className="modal fade show d-block"
        style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          marginTop: "0",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1050,
          overflowY: "auto",
        }}
        tabIndex="-1"
        onClick={() => setModalVisible(false)}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          onClick={(e) => e.stopPropagation()}
          style={{ margin: "20px auto" }}
        >
          <div className="modal-content rounded-4 border-0">
            {/* Modal Header */}
            <div className="modal-header border-bottom-0 p-4 pb-0">
              <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h5 className="modal-title fw-bold fs-4 mb-1">
                    Salon Service Details
                  </h5>
                  <p className="text-muted small mb-0">
                    Order #{selectedOrder._id?.slice(-8)?.toUpperCase()}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalVisible(false)}
                  aria-label="Close"
                ></button>
              </div>
            </div>

            <div
              className="modal-body p-4"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {/* Status Overview Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">Order Status</h6>
                <div className="text-center">
                  <OrderStatusBadge order={selectedOrder} size="large" />
                  <p className="text-muted mt-3">{statusInfo.description}</p>
                </div>
              </div>

              {/* Service Summary */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">Service Summary</h6>
                <div className="row g-3">
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Order ID</div>
                    <div className="fw-semibold">
                      {selectedOrder._id?.slice(-8)?.toUpperCase()}
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Service Date</div>
                    <div className="fw-semibold">
                      {selectedOrder.ServicePreferedDate || "Not scheduled"}
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Service Time</div>
                    <div className="fw-semibold">
                      {selectedOrder.ServicePreferedTime || "Not scheduled"}
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="text-muted small mb-1">Order Created</div>
                    <div className="fw-semibold">
                      {moment(selectedOrder.createdAt).format(
                        "DD MMM YYYY, hh:mm A"
                      )}
                    </div>
                  </div>
                  {selectedOrder.updatedAt && (
                    <div className="col-6 col-md-4">
                      <div className="text-muted small mb-1">Last Updated</div>
                      <div className="fw-semibold">
                        {moment(selectedOrder.updatedAt).format(
                          "DD MMM YYYY, hh:mm A"
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Service Partner */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">Service Partner</h6>
                <div className="bg-light p-3 rounded-3">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-3">
                      <IoPerson size={24} className="text-primary" />
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="fw-bold mb-1">{partner.name}</h6>
                      <div
                        className="text-primary mb-1 d-flex align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => openDialer(partner.number)}
                      >
                        <FaPhone size={14} className="me-1" />
                        {partner.number}
                      </div>
                      <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1">
                        {partner.status}
                      </span>
                      {partner.assignedAt && (
                        <div className="text-muted small mt-1">
                          Assigned on{" "}
                          {moment(partner.assignedAt).format("DD MMM YYYY")}
                        </div>
                      )}
                    </div>
                    {partner.number !== "To be assigned" && (
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        onClick={() => openDialer(partner.number)}
                      >
                        <FaPhone size={14} className="me-2" />
                        Call
                      </button>
                    )}
                  </div>

                  {partner.name === "To be assigned" && (
                    <div className="bg-warning bg-opacity-10 p-3 rounded-2">
                      <div className="d-flex align-items-start">
                        <IoPerson
                          size={16}
                          className="text-warning me-2 mt-1"
                        />
                        <p className="text-warning mb-0 small">
                          A qualified professional will be assigned to you
                          before your service time. You'll receive a
                          notification.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Services List */}
              {selectedOrder.OrderProducts &&
                selectedOrder.OrderProducts.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3 text-dark">
                      Services Booked{" "}
                      {hasMultipleServices &&
                        `(${selectedOrder.OrderProducts.length})`}
                    </h6>
                    <div className="list-group">
                      {selectedOrder.OrderProducts.map((product, index) => (
                        <div
                          key={index}
                          className="list-group-item border-0 p-3 mb-2 bg-light rounded-3"
                        >
                          <div className="d-flex align-items-center">
                            <img
                              src={product.ImgUrl}
                              alt={product.ProductName}
                              className="rounded-2 me-3"
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://via.placeholder.com/60x60?text=Service";
                              }}
                            />
                            <div className="flex-grow-1">
                              <h6 className="fw-bold mb-1">
                                {product.ProductName}
                              </h6>
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                  {product.Brand}
                                </span>
                                <span className="badge bg-primary bg-opacity-10 text-primary">
                                  {product.CatName}
                                </span>
                                {product.Duration && (
                                  <span className="badge bg-info bg-opacity-10 text-info">
                                    {product.Duration} mins
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Modal Footer with refresh option */}
            <div className="modal-footer border-top-0 p-4 pt-0 d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                onClick={() => setModalVisible(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 py-2 d-flex align-items-center"
                onClick={refreshOrders}
                disabled={refreshing}
              >
                {refreshing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <FaArrowsRotate className="me-2" />
                    Refresh Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Empty state component
  const EmptyState = () => (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
      <div
        className="bg-light rounded-circle d-flex align-items-center justify-content-center mb-4"
        style={{ width: "120px", height: "120px" }}
      >
        <FaHome size={48} className="text-muted" />
      </div>
      <h4 className="fw-bold mb-3">No Salon Orders</h4>
      <p className="text-muted text-center mb-4" style={{ maxWidth: "400px" }}>
        {!isAuth
          ? "Please login to view your salon orders"
          : "You haven't booked any home salon services yet. Book your first service today!"}
      </p>
      <button
        className="btn btn-primary px-5 py-3 d-flex align-items-center"
        onClick={() => navigate(!isAuth ? "/login" : "/home-salon")}
      >
        <IoSparkles className="me-2" />
        {!isAuth ? "LOGIN TO VIEW ORDERS" : "BOOK AT HOME SERVICE"}
      </button>
    </div>
  );

  // Show login prompt if not authenticated
  if (!isAuth) {
    return (
      <div className="container p-2" style={{ marginTop: "80px" }}>
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center px-2 rounded-1 py-2 shadow-sm"
          style={{ backgroundColor: "#aa2b57" }}
        >
          <h1
            className="text-white fs-6 fw-semibold m-0 text-start flex-grow-1"
            style={{ letterSpacing: "0.5px" }}
          >
            Salon Orders
          </h1>
          <div style={{ width: "40px" }}></div>
        </div>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="container p-2" style={{ marginTop: "80px" }}>
      {/* Header with refresh and back navigation */}
      <div
        className="d-flex justify-content-between align-items-center px-2 rounded-1 py-2 shadow-sm mb-3"
        style={{ backgroundColor: "#aa2b57" }}
      >
        <button
          className="btn btn-light btn-sm"
          onClick={() => navigate(-1)}
          style={{ minWidth: "40px" }}
        >
          <FaChevronLeft />
        </button>
        <h1
          className="text-white fs-6 fw-semibold m-0 text-center flex-grow-1"
          style={{ letterSpacing: "0.5px" }}
        >
          Salon Orders
        </h1>
        <button
          className="btn btn-light btn-sm d-flex align-items-center"
          onClick={refreshOrders}
          disabled={refreshing}
          style={{ minWidth: "40px" }}
        >
          {refreshing ? (
            <span className="spinner-border spinner-border-sm"></span>
          ) : (
            <FaArrowsRotate />
          )}
        </button>
      </div>

      {/* Refresh indicator */}
      {refreshing && (
        <div
          className="alert alert-info d-flex align-items-center mb-3"
          role="alert"
        >
          <FaArrowsRotate className="me-2 spinner-border spinner-border-sm" />
          Updating your orders...
        </div>
      )}

      {/* Main Content */}
      <div className="py-4">
        {homeSalonOrders?.length > 0 ? (
          <div className="row">
            {homeSalonOrders
              .slice()
              .reverse()
              .map((order, index) => (
                <div
                  key={order._id || index}
                  className="col-12 col-lg-6 col-xl-4 mb-3"
                >
                  <OrderCard order={order} />
                </div>
              ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Modal */}
      {modalVisible && <OrderDetailsModal />}
    </div>
  );
};

export default SalonOrders;
